import { create } from "zustand";
import { api, getToken, setToken } from "../lib/api";
import type { CurrentUser } from "../lib/types";

interface AuthState {
  user: CurrentUser | null;
  isAuthenticated: boolean;
  isInitializing: boolean;
  /** Signed in, but not on the platform team — shown a dead end, not the console. */
  isForbidden: boolean;
  initialize: () => Promise<void>;
  signIn: (token: string, user: CurrentUser) => void;
  signOut: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isAuthenticated: false,
  isInitializing: true,
  isForbidden: false,

  initialize: async () => {
    if (!getToken()) {
      set({ isInitializing: false });
      return;
    }
    try {
      const data = await api.get<{ user: CurrentUser }>("/auth/me");
      set({
        user: data.user,
        isAuthenticated: Boolean(data.user.is_superadmin),
        isForbidden: !data.user.is_superadmin,
        isInitializing: false,
      });
    } catch {
      setToken(null);
      set({ user: null, isAuthenticated: false, isInitializing: false });
    }
  },

  signIn: (token, user) => {
    setToken(token);
    set({
      user,
      isAuthenticated: Boolean(user.is_superadmin),
      isForbidden: !user.is_superadmin,
      isInitializing: false,
    });
  },

  signOut: async () => {
    try {
      await api.del("/auth/logout");
    } catch {
      // The session may already be gone server-side; local state is what matters.
    }
    setToken(null);
    set({ user: null, isAuthenticated: false, isForbidden: false });
  },
}));
